import { useMutation, useQuery } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";

import { acceptInvite, getInviteInfo } from "@/api/auth";
import { setTokens } from "@/api/client";
import { SkeletonText } from "@/components/ui/Skeleton";

export default function AcceptInvitePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token = "" } = useParams<{ token: string }>();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [fullName, setFullName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const infoQuery = useQuery({
    queryKey: ["invite-info", token],
    queryFn: () => getInviteInfo(token),
    retry: false,
    enabled: Boolean(token),
  });

  const accept = useMutation({
    mutationFn: () =>
      acceptInvite({ token, password, full_name: fullName || undefined }),
    onSuccess: ({ access_token, refresh_token }) => {
      setTokens(access_token, refresh_token);
      void navigate("/", { replace: true });
    },
  });

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError(t("invite.error.password_short"));
      return;
    }
    if (password !== confirm) {
      setError(t("invite.error.password_mismatch"));
      return;
    }
    accept.mutate();
  }

  if (infoQuery.isLoading) {
    return (
      <CenteredCard>
        <SkeletonText lines={4} />
      </CenteredCard>
    );
  }

  if (infoQuery.error) {
    const status = (infoQuery.error as { response?: { status?: number } })
      .response?.status;
    return (
      <CenteredCard>
        <h1 className="text-xl font-semibold">{t("invite.invalid.title")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {status === 404
            ? t("invite.invalid.not_found")
            : status === 410
              ? t("invite.invalid.expired_or_used")
              : t("invite.invalid.generic")}
        </p>
      </CenteredCard>
    );
  }

  const info = infoQuery.data!;

  return (
    <CenteredCard>
      <div className="mb-4 text-center">
        <h1 className="text-2xl font-bold">{t("invite.title")}</h1>
        <p className="text-sm text-muted-foreground">
          {t("invite.subtitle", { email: info.email })}
        </p>
        {info.is_admin && (
          <span className="mt-2 inline-block rounded bg-amber-100 px-2 py-0.5 text-xs uppercase text-amber-900">
            {t("invite.admin_badge")}
          </span>
        )}
      </div>

      <form onSubmit={submit} className="space-y-4">
        <div>
          <label htmlFor="email" className="mb-1 block text-sm font-medium">
            {t("auth.login.email")}
          </label>
          <input
            id="email"
            type="email"
            value={info.email}
            disabled
            className="w-full rounded-md border border-input bg-muted px-3 py-2 text-sm text-muted-foreground"
          />
        </div>

        <div>
          <label htmlFor="full_name" className="mb-1 block text-sm font-medium">
            {t("invite.full_name")}
          </label>
          <input
            id="full_name"
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder={info.full_name ?? ""}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label htmlFor="password" className="mb-1 block text-sm font-medium">
            {t("invite.password")}
          </label>
          <input
            id="password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            autoComplete="new-password"
            minLength={8}
          />
        </div>

        <div>
          <label htmlFor="confirm" className="mb-1 block text-sm font-medium">
            {t("invite.confirm_password")}
          </label>
          <input
            id="confirm"
            type="password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            autoComplete="new-password"
            minLength={8}
          />
        </div>

        {error && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}
        {accept.error && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {(accept.error as Error).message}
          </div>
        )}

        <button
          type="submit"
          disabled={accept.isPending}
          className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
        >
          {accept.isPending ? t("invite.submitting") : t("invite.submit")}
        </button>
      </form>
    </CenteredCard>
  );
}

function CenteredCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <div className="w-full max-w-sm rounded-lg border bg-card p-8 shadow-sm">
        {children}
      </div>
    </div>
  );
}
